import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useFormik } from 'formik';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { IconButton, Typography, useEventCallback } from '@mui/material';
import { ManagementPecCrumbs } from 'civil-status/constants/breadcrumb';
import FiltersPec, {
  validationSchemaFilterPec,
} from 'shell/components/shared/management-pec/pec-filters';
import { FiltersFormPec, initialValuesPec, PecClient } from 'shell/types/types-pec';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { PecComunicazioneVO } from 'shell/models/pec';
import { transformResponseListPec, transformDataset } from 'shell/utils/utils-management-pec';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { headCells } from 'shell/constants/constants-management-pec';
import { generatePath, useNavigate } from 'react-router-dom';
import { Edit, Visibility } from '@mui/icons-material';

const ManagementPecPage = (): React.ReactElement => {
  const filterTable = useFilterTable<FiltersFormPec>();

  const [pec, getPec] = useHttpFetch<PecComunicazioneVO, Array<PecClient>>(
    `${__ACT_API_URL__}/pec/ricerca`,
    {
      onBootstrap: false,
      transformer: transformResponseListPec,
    }
  );

  const pecList = (pec.data as Array<PecClient>) || [];

  const onSubmit = useEventCallback((formValues: FiltersFormPec) => {
    const queryString = transformDataset({ ...formValues, page: 0 });
    filterPec.setFieldValue('page', 0);
    getPec({ params: {}, paramsSerializer: () => queryString });
  });

  const filterPec = useFormik<FiltersFormPec>({
    initialValues: initialValuesPec,
    validationSchema: validationSchemaFilterPec,
    onSubmit,
  });

  useEffect(() => {
    if (!filterTable.values?.hasVisitedChild) {
      return;
    }
    filterPec.setValues(filterTable.values || initialValuesPec);
    const queryString = transformDataset(filterTable.values || initialValuesPec);
    getPec({ params: {}, paramsSerializer: () => queryString });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterPec.values);
  }, [filterPec.values]);

  const handleChangePage = useEventCallback((newPage) => {
    filterPec.setFieldValue('page', newPage);
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage) => {
    const newDataset = {
      ...filterPec.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };
    filterPec.setValues(newDataset as FiltersFormPec);
  });

  const navigate = useNavigate();

  const onVisibilityPress = useCallback(
    (cell): Promise<PecClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });
        navigate(generatePath('/stato-civile/gestione-pec/dettaglio/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate, filterTable]
  );

  const onEditPress = useCallback(
    (cell): Promise<PecClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });
        navigate(generatePath('/stato-civile/gestione-pec/modifica/:id', { id: cell.code }));
        resolve(cell);
      });
    },
    [navigate, filterTable]
  );

  const body: Array<PecClient> = useMemo(() => {
    return (pecList || []).map((item) => {
      return {
        ...item,
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onVisibilityPress(item);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Visibility />
            </IconButton>
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEditPress(item);
              }}
              aria-label="Modifica elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Edit />
            </IconButton>
          </div>
        ),
      };
    });
  }, [pecList]);

  return (
    <Fragment>
      <Breadcrumbs items={ManagementPecCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Gestione PEC
        </Typography>
      </div>
      <div className="pmi-my-3">
        <FiltersPec filters={filterPec} />
      </div>
      {pec.data ? (
        pecList?.length > 0 ? (
          <Table
            body={body}
            onSort={noop}
            noSort
            head={headCells}
            orders={filterPec.values.orders || {}}
            page={filterPec.values.page || 0}
            rowsPerPage={filterPec.values.rowsPerPage}
            onVisibilityPress={noop}
            onEditPress={noop}
            onTrashPress={noop}
            count={pecList.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista PEC"
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ManagementPecPage.displayName = 'Pec Management page component';

export default ManagementPecPage;
